import { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import SectionTitle from "../SectionTitle/SectionTitle";
import axios from "axios";
import {
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaLeaf,
  FaTree,
  FaWater,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import StarLoader from "../loader/StarLoader";

const settings = {
  dots: false,
  arrows: true,
  speed: 1000,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  responsive: [
    {
      breakpoint: 1400,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
        infinite: true,
      },
    },
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        infinite: true,
        centerMode: false,
      },
    },
    {
      breakpoint: 991,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        centerMode: false,
      },
    },
    {
      breakpoint: 767,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: false,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: false,
      },
    },
  ],
};

const stripHtmlTags = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, ""); // removes all HTML tags
};

const FarmSection = () => {
  const [farms, setFarms] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [maxHeight, setMaxHeight] = useState(0);
  const itemRefs = useRef([]);
  const apiUrl = process.env.REACT_APP_PUBLIC_API_URL;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getFarms = async () => {
      try {
        const res = await axios.get(`${apiUrl}/api/farms-list`);
        setFarms(res.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false); // ✅ stop loader after fetch completes
      }
    };
    getFarms();
  }, [apiUrl]);

  useEffect(() => {
    if (farms.length > 0 && itemRefs.current.length > 0) {
      // Wait for DOM to render images first
      setTimeout(() => {
        const heights = itemRefs.current.map((el) => el?.offsetHeight || 0);
        const tallest = Math.max(...heights);
        setMaxHeight(tallest);
      }, 300);
    }
  }, [farms]);

  const toggleDescription = (index) => {
    setExpandedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (loading) {
    return <StarLoader />;
  }

  if (!farms || farms.length === 0) {
    return (
      <section className="section-padding" style={{ textAlign: "center" }}>
        <p>No farm datas found.</p>
      </section>
    );
  }

  return (
    <section
      className="wpo-service-section-s2 section-padding"
      style={{
        background:
          "linear-gradient(135deg, #f0f9ff 0%, #f0f9ff 50%, #f0f9ff 100%)",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#E6F7FF",
        paddingTop: "40px", // reduce vertical padding
        paddingBottom: "40px",
      }}
    >
      {/* Background Decorative Elements */}
      <div
        style={{
          position: "absolute",
          top: "10%",
          left: "5%",
          width: "100px",
          height: "100px",
          background:
            "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)",
          borderRadius: "50%",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "15%",
          right: "8%",
          width: "150px",
          height: "150px",
          background:
            "radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)",
          borderRadius: "50%",
        }}
      />

      <div className="container">
        <SectionTitle
          subTitle={"Our Organic Farms"}
          Title={"Sustainable Agriculture"}
        />
        <div className="row-grid wpo-service-slider-s2">
          <Slider {...settings}>
            {farms.map((farm, index) => {
              const isHovered = hoveredIndex === index;
              const isExpanded = expandedCards[index];
              const description =
                stripHtmlTags(farm.description) || "No description available";
              const shortDesc =
                description.length > 100
                  ? description.slice(0, 100) + "..."
                  : description;
              const showReadMore = description.length > 100;

              return (
                <div className="grid" key={index}>
                  <div
                    ref={(el) => (itemRefs.current[index] = el)}
                    style={{
                      background:
                        "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
                      borderRadius: "20px",
                      overflow: "hidden",
                      transition:
                        "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                      boxShadow: isHovered
                        ? "0 25px 50px rgba(59, 130, 246, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.1)"
                        : "0 10px 25px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)",
                      transform: isHovered
                        ? "translateY(-8px) scale(1.02)"
                        : "translateY(0) scale(1)",
                      margin: "20px 15px",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      border: "1px solid rgba(59, 130, 246, 0.1)",
                      height:
                        maxHeight && !isExpanded ? `${maxHeight}px` : "auto",
                      minHeight: maxHeight ? `${maxHeight}px` : "auto",
                    }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {/* Organic Badge */}
                    <div
                      style={{
                        position: "absolute",
                        top: "16px",
                        right: "16px",
                        zIndex: 10,
                        background: "linear-gradient(45deg, #3b82f6, #1d4ed8)",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "11px",
                        fontWeight: "700",
                        color: "#fff",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <FaLeaf size={10} />
                      Organic
                    </div>

                    {/* Image Section */}
                    <div
                      style={{
                        position: "relative",
                        height: "160px", // Reduced from 240px
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={`${apiUrl}/storage/` + farm.image}
                        alt={farm.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transform: isHovered ? "scale(1.1)" : "scale(1)",
                          transition: "transform 0.6s ease",
                          display: "block",
                        }}
                      />
                      {/* Gradient Overlay */}
                      <div
                        style={{
                          position: "absolute",
                          bottom: "0",
                          left: "0",
                          width: "100%",
                          height: "50%",
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 100%)",
                        }}
                      />
                    </div>

                    {/* Farm Name - Positioned in the middle */}
                    <div
                      style={{
                        position: "relative",
                        zIndex: 5,
                        marginTop: "-20px",
                        marginBottom: "16px",
                        padding: "0 20px",
                      }}
                    >
                      <div
                        style={{
                          background: "#ffffff",
                          padding: "12px 16px",
                          borderRadius: "12px",
                          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12)",
                          border: isHovered
                            ? "2px solid #3b82f6"
                            : "2px solid #f1f5f9",
                          transform: isHovered
                            ? "translateY(-3px) scale(1.03)"
                            : "translateY(0) scale(1)",
                          transition: "all 0.3s ease",
                          textAlign: "center",
                        }}
                      >
                        <h2
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: isHovered ? "#3b82f6" : "#1e40af",
                            margin: "0",
                            lineHeight: "1.3",
                            transition: "color 0.3s ease",
                          }}
                        >
                          {farm.name}
                        </h2>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div
                      style={{
                        padding: "0 20px 20px 20px",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Description with Expandable Feature */}
                      <div
                        style={{
                          flex: 1,
                          marginBottom: "6px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#64748b",
                            lineHeight: "1.6",
                            margin: "0 0 2px 0",
                            transition: "all 0.3s ease",
                          }}
                        >
                          {isExpanded ? description : shortDesc}
                        </p>

                        {/* Read More/Less Button */}
                        {showReadMore && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDescription(index);
                            }}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "#3b82f6",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: "pointer",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              transition: "all 0.2s ease",
                              marginBottom: isExpanded ? "2px" : "0",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background =
                                "rgba(59, 130, 246, 0.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "transparent";
                            }}
                          >
                            {isExpanded ? (
                              <>
                                <FaChevronUp size={10} />
                                Show Less
                              </>
                            ) : (
                              <>
                                <FaChevronDown size={10} />
                                Read More
                              </>
                            )}
                          </button>
                        )}
                      </div>

                      {/* Farm Details */}
                      <div
                        style={{
                          borderTop: "1px solid rgba(59, 130, 246, 0.1)",
                          paddingTop: isExpanded ? "2px" : "0",
                          marginBottom: "8px",
                        }}
                      >
                        {/* Location */}
                        {(farm.location || farm.address) && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "10px",
                              marginBottom: "12px",
                            }}
                          >
                            <FaMapMarkerAlt
                              size={13}
                              color="#3b82f6"
                              style={{ marginTop: "2px", flexShrink: 0 }}
                            />
                            <span
                              style={{
                                fontSize: "13px",
                                color: "#475569",
                                lineHeight: "1.4",
                              }}
                            >
                              {farm.location ? (
                                <>
                                  <strong style={{ color: "#1e293b" }}>
                                    {farm.location}
                                  </strong>
                                  {farm.address ? ` (${farm.address})` : null}
                                </>
                              ) : (
                                <>{farm.address}</>
                              )}
                            </span>
                          </div>
                        )}

                        {/* Size */}
                        {farm.size && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "12px",
                            }}
                          >
                            <FaTree
                              size={13}
                              color="#f59e0b"
                              style={{ flexShrink: 0 }}
                            />
                            <span
                              style={{
                                fontSize: "13px",
                                color: "#475569",
                              }}
                            >
                              <strong style={{ color: "#1e293b" }}>
                                Size:
                              </strong>{" "}
                              {farm.size}
                            </span>
                          </div>
                        )}

                        {/* Crops */}
                        {farm.crops && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "12px",
                            }}
                          >
                            <FaWater
                              size={13}
                              color="#8b5cf6"
                              style={{ flexShrink: 0 }}
                            />
                            <span
                              style={{
                                fontSize: "13px",
                                color: "#475569",
                              }}
                            >
                              <strong style={{ color: "#1e293b" }}>
                                Crops:
                              </strong>{" "}
                              {farm.crops}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Map Link */}
                      {farm.map_url && (
                        <a
                          href={farm.map_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            padding: "10px 20px",
                            borderRadius: "8px",
                            background: isHovered
                              ? "linear-gradient(135deg, #1d4ed8, #3b82f6)"
                              : "#f1f5f9",
                            color: isHovered ? "#ffffff" : "#3b82f6",
                            textDecoration: "none",
                            fontSize: "14px",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            border: "1px solid #e2e8f0",
                            boxShadow: isHovered
                              ? "0 6px 18px rgba(59, 130, 246, 0.4)"
                              : "0 2px 8px rgba(0, 0, 0, 0.05)",
                            transform: isHovered
                              ? "translateY(-3px)"
                              : "translateY(0)",
                            marginTop: "auto",
                          }}
                        >
                          <FaExternalLinkAlt size={12} />
                          View on Map
                        </a>
                      )}
                    </div>

                    {/* Hover Border Effect */}
                    <div
                      style={{
                        position: "absolute",
                        top: "0",
                        left: "0",
                        right: "0",
                        bottom: "0",
                        border: "2px solid #3b82f6",
                        borderRadius: "20px",
                        opacity: isHovered ? 1 : 0,
                        transition: "opacity 0.3s ease",
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
    </section>
  );
};

export default FarmSection;
